import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  HostListener,
  inject,
  Input,
  Output,
  ViewChild,
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ToastrService } from 'ngx-toastr';
import { ActivityTrackerService } from '@services/user-activity/activity-tracker.service';
import { RemmiConfirmService } from '@common/remmi-confirm/remmi-confirm.service';
import {
  ChatAttachment,
  ChatDeliveryState,
  ChatMessage,
  ChatReaction,
  CHAT_EDIT_WINDOW_MS,
  CHAT_QUICK_REACTIONS,
  formatFileSize,
  listNames,
  systemEventText,
} from '../../models/chat.models';
import { ChatStore } from '../../services/chat.store';
import { durationLabel, timeLabel } from '../../utils/chat-date.util';
import { ChatBodySegment, linkifyBody } from '../../utils/chat-link.util';
import { calendarTemplateUrl, ChatCallLink, extractCallLink, isCallStartedMessage } from '../../utils/chat-call-link.util';
import { ChatCallOutcome } from '../../models/chat.models';
import { ChatAvatarComponent } from '../chat-avatar/chat-avatar.component';

/**
 * One message bubble: body, attachments, reactions and the hover actions.
 *
 * Renders three quite different things and keeps them in one component because they share
 * alignment and hover behaviour: a normal message, a deleted tombstone, and a system event
 * ("Sarah added Michael"), which is centred grey text with no bubble at all.
 */
@Component({
  selector: 'app-chat-message',
  standalone: true,
  imports: [CommonModule, FormsModule, ChatAvatarComponent],
  templateUrl: './chat-message.component.html',
  styleUrls: ['./chat-message.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChatMessageComponent {
  private readonly store = inject(ChatStore);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly host: ElementRef<HTMLElement> = inject(ElementRef);
  private readonly toastr = inject(ToastrService);
  private readonly activityTracker = inject(ActivityTrackerService);
  private readonly remmiConfirm = inject(RemmiConfirmService);

  @ViewChild('editBox') private editBox?: ElementRef<HTMLTextAreaElement>;
  @ViewChild('picker') private picker?: ElementRef<HTMLElement>;
  @ViewChild('menu') private menu?: ElementRef<HTMLElement>;

  @Input({ required: true }) message!: ChatMessage;

  /** True when the previous bubble is from the same person minutes earlier — hides the avatar/name. */
  @Input() grouped = false;

  /** The group owner can delete anyone's message in their group. */
  @Input() canModerate = false;

  @Output() reply = new EventEmitter<ChatMessage>();

  readonly quickReactions = CHAT_QUICK_REACTIONS;
  readonly timeLabel = timeLabel;
  readonly durationLabel = durationLabel;
  readonly formatFileSize = formatFileSize;

  menuOpen = false;
  reactionPickerOpen = false;
  editing = false;
  draft = '';

  get isOwn(): boolean {
    return this.message.sender.user_id === this.store.myId;
  }

  get isSystem(): boolean {
    return !!this.message.system_event;
  }

  /**
   * The centred grey line. Built from `system_meta`, not `body` — the server sends system rows
   * with an empty body and the names in the meta, captured at the time of the event.
   */
  get systemText(): string {
    return systemEventText(this.message);
  }

  /** Editing is the sender's own, within the server's 15-minute window, and not a deleted message. */
  get canEdit(): boolean {
    if (!this.isOwn || this.message.is_deleted || this.message.pending) {
      return false;
    }
    return Date.now() - new Date(this.message.created_at).getTime() < CHAT_EDIT_WINDOW_MS;
  }

  get canDelete(): boolean {
    return !this.message.is_deleted && !this.message.pending && (this.isOwn || this.canModerate);
  }

  /**
   * Anyone's message can be forwarded, not just your own — passing on what a colleague said is
   * the usual reason to want this at all. Only a tombstone or a bubble the server hasn't
   * acknowledged yet is excluded: there is nothing to copy in the first, and no id in the second.
   */
  get canForward(): boolean {
    return !this.message.is_deleted && !this.message.pending && !this.message.failed && !this.isSystem;
  }

  get seenBy(): string[] {
    if (!this.isOwn || this.message.pending) {
      return [];
    }
    return this.store.seenNamesFor(this.message);
  }

  get seenLabel(): string {
    const names = this.seenBy;
    if (!names.length) {
      return '';
    }
    if (names.length <= 3) {
      return `Seen by ${names.join(', ')}`;
    }
    return `Seen by ${names.slice(0, 2).join(', ')} and ${names.length - 2} others`;
  }

  // ---- Delivery ticks --------------------------------------------------------

  /** Which tick to draw under your own bubble. Meaningless on someone else's message. */
  get deliveryState(): ChatDeliveryState | null {
    return this.isOwn ? this.store.deliveryStateFor(this.message) : null;
  }

  /** Two ticks the moment anyone has read it; the colour is what separates "some" from "all". */
  get showDoubleTick(): boolean {
    const state = this.deliveryState;
    return state === 'read' || state === 'read-all';
  }

  /**
   * The tick's tooltip, which is where the distinction actually gets spelled out — an icon can
   * only carry so much, and "why is this one still grey" is a fair question in a group.
   */
  get deliveryLabel(): string {
    switch (this.deliveryState) {
      case 'sent':
        return 'Sent';
      case 'read':
        return this.seenLabel || 'Read';
      case 'read-all':
        return this.seenLabel || 'Read by everyone';
      default:
        return '';
    }
  }

  // ---- Body ------------------------------------------------------------------

  /**
   * The body split into plain and linked runs.
   *
   * Recomputed per change-detection pass rather than cached: a body only changes on an edit, and
   * memoising it would need invalidation keyed on that. The regex runs over a string capped at
   * 8000 chars, which is nothing next to the DOM work of rendering the bubble around it.
   */
  get bodySegments(): ChatBodySegment[] {
    return linkifyBody(this.message.body);
  }

  trackBySegment = (index: number, segment: ChatBodySegment) => `${index}:${segment.href ?? ''}`;

  // ---- Reactions -------------------------------------------------------------

  /**
   * "You, Sarah and Michael reacted with 👍" — the hover tooltip on a reaction pill.
   *
   * Capped at three names because the tooltip is a glance, not a guest list: a pill with fifteen
   * reactors would otherwise produce a paragraph hovering over the transcript.
   */
  reactionTooltip(reaction: ChatReaction): string {
    const names = this.store.reactionNamesFor(reaction);
    if (!names.length) {
      return '';
    }
    const shown = names.length <= 3 ? listNames(names) : `${names.slice(0, 3).join(', ')} and ${names.length - 3} others`;
    return `${shown} reacted with ${reaction.emoji}`;
  }

  // ---- Video calls -----------------------------------------------------------

  /** The call link in this message, if it has one — turns the bubble into a call card. */
  get callLink(): ChatCallLink | null {
    return this.message.is_deleted ? null : extractCallLink(this.message.body);
  }

  /**
   * The card's headline.
   *
   * Only a message that actually started a call says so. A re-share of a call already running was
   * not started by whoever pressed the button, and a link somebody pasted was not started by
   * anyone — both read as sharing, which is what they are.
   */
  get callTitle(): string {
    const who = this.isOwn ? 'You' : this.message.sender.name;
    return isCallStartedMessage(this.message.body) ? `${who} started a video call` : `${who} shared a call link`;
  }

  /** What the card prints under its title: the provider, then the room code or the lobby. */
  get callLabel(): string {
    const link = this.callLink;
    if (!link) {
      return '';
    }
    return `${link.providerName} · ${link.instant ? 'instant meeting' : link.label}`;
  }

  /**
   * What happened to this call, as far as this browser saw.
   *
   * Never shown on your own call: "you missed your own call" is nonsense, and the initiator's
   * outcome is not something we track. Meet tells nobody who joined, so this only ever speaks
   * for the person reading it.
   */
  get callOutcome(): ChatCallOutcome | null {
    return this.isOwn ? null : this.store.callOutcomeFor(this.message.message_id);
  }

  get callOutcomeLabel(): string {
    switch (this.callOutcome) {
      case 'joined':
        return 'You joined';
      case 'ignored':
        return 'You ignored this call';
      case 'missed':
        return 'You missed this call';
      default:
        return '';
    }
  }

  /** Clicking Join is this browser watching itself answer, which is the only outcome we can know. */
  onJoinCall(): void {
    this.store.recordCallOutcome(this.message.message_id, 'joined');
  }

  /**
   * Copy the link rather than the whole message, which is what someone reaching for this actually
   * wants — pasting it into an email or a calendar invite of their own.
   */
  copyCallLink(): void {
    const link = this.callLink;
    if (!link) {
      return;
    }
    // `navigator.clipboard` is undefined on an insecure origin, so this is a real branch, not paranoia.
    const write = navigator.clipboard?.writeText(link.url);
    if (!write) {
      this.toastr.error('Copying is not available in this browser.');
      return;
    }
    write.then(
      () => this.toastr.success('Call link copied.'),
      () => this.toastr.error('The link could not be copied.')
    );
  }

  /**
   * Put the call in the reader's own calendar.
   *
   * Deliberately Google's public template URL rather than the Calendar API the call button uses:
   * this action belongs to whoever *received* the link, who may never have connected their Google
   * account, and a pre-filled event they save themselves needs no connection at all.
   */
  addCallToCalendar(): void {
    const link = this.callLink;
    if (!link) {
      return;
    }
    const title = this.store.activeTitle();
    window.open(calendarTemplateUrl(link.url, title ? `Call — ${title}` : 'Call'), '_blank', 'noopener,noreferrer');
  }

  // ---- Actions ---------------------------------------------------------------

  toggleMenu(): void {
    this.menuOpen = !this.menuOpen;
    this.reactionPickerOpen = false;
    this.keepInView(() => this.menu);
  }

  toggleReactionPicker(): void {
    this.reactionPickerOpen = !this.reactionPickerOpen;
    this.menuOpen = false;
    this.keepInView(() => this.picker);
  }

  /** Applied to whichever popover is open; `none` when it already fits where it was anchored. */
  get shiftStyle(): string {
    return this.popoverShift ? `translateX(${this.popoverShift}px)` : 'none';
  }

  private popoverShift = 0;

  /**
   * Nudge an open popover back inside the transcript.
   *
   * Each popover is anchored to its own button, which is what makes it feel attached — but a
   * 180px picker on a two-character message near the left edge, or on a full-width message near
   * the right, would hang off the side of a 380px widget and get clipped. Measuring after it
   * renders and translating it back is the only way to keep both: anchored to the button, and
   * fully on screen.
   */
  private keepInView(target: () => ElementRef<HTMLElement> | undefined): void {
    this.popoverShift = 0;
    setTimeout(() => {
      const element = target()?.nativeElement;
      const bounds = element?.closest('.chat-list')?.getBoundingClientRect();
      if (!element || !bounds) {
        return;
      }
      const rect = element.getBoundingClientRect();
      const overflowLeft = bounds.left + ChatMessageComponent.EDGE_GAP_PX - rect.left;
      const overflowRight = rect.right - (bounds.right - ChatMessageComponent.EDGE_GAP_PX);

      this.popoverShift = overflowLeft > 0 ? overflowLeft : overflowRight > 0 ? -overflowRight : 0;
      this.cdr.markForCheck();
    });
  }

  /** Breathing room left between a popover and the edge of the transcript. */
  private static readonly EDGE_GAP_PX = 8;

  react(emoji: string): void {
    this.store.toggleReaction(this.message, emoji);
    this.reactionPickerOpen = false;
  }

  startEdit(): void {
    this.draft = this.message.body;
    this.editing = true;
    this.menuOpen = false;
    setTimeout(() => {
      const el = this.editBox?.nativeElement;
      el?.focus();
      el?.setSelectionRange(el.value.length, el.value.length);
    });
  }

  cancelEdit(): void {
    this.editing = false;
    this.draft = '';
  }

  saveEdit(): void {
    const next = this.draft.trim();
    if (next && next !== this.message.body) {
      this.store.editMessage(this.message, next);
    }
    this.cancelEdit();
  }

  onEditKeydown(event: KeyboardEvent): void {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      this.saveEdit();
      return;
    }
    if (event.key === 'Escape') {
      event.preventDefault();
      this.cancelEdit();
    }
  }

  remove(): void {
    this.menuOpen = false;
    this.remmiConfirm
      .delete({ title: 'Delete Message?', message: 'This message will be deleted for everyone in the conversation.' })
      .subscribe(isConfirmed => {
        if (isConfirmed) {
          this.store.deleteMessage(this.message);
        }
      });
  }

  /** Hands the message to the store; the picker is rendered at the top of the page, not here. */
  forwardMessage(): void {
    this.menuOpen = false;
    this.store.beginForward(this.message);
  }

  retry(): void {
    this.store.retry(this.message);
  }

  // ---- Attachments -----------------------------------------------------------

  /**
   * For rendering in place — `<img>`, `<audio>`, or opening full size in a tab.
   *
   * The signed `url` is served inline, which is what those need. Neither element can carry an
   * Authorization header, so a bearer-protected route is never an option here.
   */
  attachmentUrl(attachment: ChatAttachment): string {
    return attachment?.url || this.store.downloadUrl(attachment);
  }

  /**
   * For the download button, which is a different URL to the preview one: `download_url` is the
   * same object signed with `Content-Disposition: attachment`, so the file **saves** instead of
   * opening in a tab. Falls back to the inline URL rather than to a route an anchor cannot use.
   */
  downloadHref(attachment: ChatAttachment): string {
    return this.store.downloadUrl(attachment) || attachment?.url || '';
  }

  /** Images open full size in a new tab — V1 has no lightbox and doesn't need one. */
  openAttachment(attachment: ChatAttachment): void {
    const url = this.attachmentUrl(attachment);
    if (url) {
      this.activityTracker.track('document_download', { meta: { count: 1, module: 'chat' } });
      window.open(url, '_blank', 'noopener');
    }
  }

  /**
   * A thumbnail that fails to load is almost always an expired URL, not a missing file: the
   * server signs attachment URLs for one hour, so a thread left open past that has a screen of
   * broken images. Asking the store to re-mint them is the fix; it throttles the request itself,
   * and `imageFailed` keeps a genuinely dead image from looping on its own error event.
   */
  onImageError(attachment: ChatAttachment): void {
    // Keyed by URL as well as id, so the *next* expiry an hour later is still allowed to ask.
    const key = `${attachment.attachment_id}|${attachment.url}`;
    if (this.imageFailed.has(key)) {
      return;
    }
    this.imageFailed.add(key);
    this.store.refreshAttachments(this.message.conversation_id);
  }

  private readonly imageFailed = new Set<string>();

  trackByReaction = (_: number, reaction: ChatReaction) => reaction.emoji;
  trackByAttachment = (_: number, attachment: ChatAttachment) => attachment.attachment_id;

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if ((this.menuOpen || this.reactionPickerOpen) && !this.host.nativeElement.contains(event.target as Node)) {
      this.menuOpen = false;
      this.reactionPickerOpen = false;
      this.cdr.markForCheck();
    }
  }
}
